import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";

// P6G — avanzamento run segmentato hardened (P1.7.1), responsive.
// Desktop: tabella in contenitore scorrevole. Mobile: card verticali per
// ogni segmento S0-S6 (nessun campo eliminato). Read-only: solo polling
// GET, nessuna POST, nessun pulsante di retry.
const STATE_STYLES = {
  NOT_STARTED: "bg-gray-100 text-gray-700",
  PLANNED: "bg-gray-100 text-gray-700",
  CLAIMED: "bg-amber-100 text-amber-900",
  POST_IN_PROGRESS: "bg-blue-100 text-blue-900 animate-pulse",
  PROVIDER_ACCEPTED: "bg-indigo-100 text-indigo-900",
  POLLING: "bg-blue-100 text-blue-900 animate-pulse",
  POLLING_DEFERRED: "bg-yellow-200 text-yellow-950",
  POLLING_DEFERRED_REQUIRES_OWNER: "bg-orange-200 text-orange-950",
  COMPLETED: "bg-emerald-100 text-emerald-900",
  FAILED_BEFORE_POST: "bg-red-100 text-red-900",
  PROVIDER_TERMINAL_FAILURE: "bg-red-200 text-red-950",
  SUBMITTED_RESULT_UNKNOWN: "bg-fuchsia-200 text-fuchsia-950",
  BLOCKED: "bg-red-200 text-red-950",
};

const StateBadge = ({ st, sid }) => (
  <span className={`inline-block px-1 py-0.5 font-bold ${STATE_STYLES[st] || ""}`}
        data-testid={`segmented-hardened-state-${sid}`}>{st}</span>
);

const Field = ({ label, children }) => (
  <div className="flex justify-between gap-2 py-0.5 border-b border-border/60 last:border-0">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className="text-right break-anywhere font-medium">{children}</span>
  </div>
);

export const SegmentedRunProgressHardened = ({ jobId, onStatus,
                                               initialSegments,
                                               statusPath = "segmented-run-canonical/status" }) => {
  const [d, setD] = useState(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!jobId) return undefined;
    const poll = async () => {
      try {
        const { data } = await api.get(
          `/reader/jobs/${jobId}/${statusPath}`);
        setD(data);
        setStatusFailed(false);
        onStatus?.(data);
      } catch { setStatusFailed(true); }
    };
    poll();
    timer.current = setInterval(poll, 5000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, statusPath]);

  // Skeleton provvisorio dai dati STATICI dell'autorizzazione (elenco
  // segmenti, tipo, codice, pagine, immagini), finche' il vero endpoint
  // /status non risponde. Nessuno stato runtime (POST/GET/token/costo/
  // lease/response_id) viene inventato o assunto.
  if (!d) {
    if (!(initialSegments && initialSegments.length)) {
      if (statusFailed) {
        return (
          <div className="border border-border p-2 text-[11px] text-muted-foreground break-anywhere"
               data-testid="segmented-hardened-status-unavailable">
            Stato temporaneamente non disponibile.
          </div>
        );
      }
      return null;
    }
    const loadingMsg = statusFailed
      ? "Stato temporaneamente non disponibile"
      : "Caricamento stato aggiornato…";
    return (
      <div className="w-full min-w-0 border border-border p-2 space-y-2 max-w-full break-anywhere"
           data-testid="segmented-hardened-progress-panel">
        <div className="font-bold flex items-center gap-1.5 flex-wrap">
          <ShieldCheck size={13} className="shrink-0" />Run segmentato temprato
        </div>
        <div className={`flex items-center gap-1.5 text-[11px] px-2 py-1 border ${statusFailed ? "border-amber-400 bg-amber-50 text-amber-900" : "border-blue-300 bg-blue-50 text-blue-900"}`}
             data-testid="segmented-hardened-status-loading">
          {statusFailed ? <AlertTriangle size={12} className="shrink-0" />
            : <Loader2 size={12} className="shrink-0 animate-spin" />}
          <span>{loadingMsg}</span>
        </div>
        {/* Desktop: struttura statica in tabella scorrevole */}
        <div className="hidden sm:block table-scroll" data-testid="segmented-hardened-table">
          <table className="w-full text-[10px]">
            <thead><tr className="border-b border-border bg-muted text-left">
              {["Seg", "Tipo", "Codice", "Pagine", "Img", "Stato"].map((h) =>
                <th key={h} className="px-1.5 py-1 font-medium whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {initialSegments.map((s) => (
                <tr key={s.segment_id} className="border-b border-border"
                    data-testid={`segmented-hardened-row-${s.segment_id}`}>
                  <td className="px-1.5 py-1 font-bold whitespace-nowrap">{s.segment_id}</td>
                  <td className="px-1.5 py-1 whitespace-nowrap">{s.kind}</td>
                  <td className="px-1.5 py-1 whitespace-nowrap">{s.component_code || "—"}</td>
                  <td className="px-1.5 py-1">{(s.pages || []).join(",")}</td>
                  <td className="px-1.5 py-1">{s.n_images ?? "—"}</td>
                  <td className="px-1.5 py-1 text-muted-foreground italic">{loadingMsg}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile: una card statica per segmento */}
        <div className="sm:hidden space-y-2" data-testid="segmented-hardened-cards">
          {initialSegments.map((s) => (
            <div key={s.segment_id} className="border border-border p-2 text-[11px]"
                 data-testid={`segmented-hardened-card-${s.segment_id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-bold">{s.segment_id}
                  <span className="font-normal text-muted-foreground"> {s.component_code || ""}</span></span>
                <span className="text-[10px] text-muted-foreground italic shrink-0">{loadingMsg}</span>
              </div>
              <Field label="Tipo">{s.kind}</Field>
              <Field label="Pagine">{(s.pages || []).join(", ") || "—"}</Field>
              <Field label="Immagini">{s.n_images ?? "—"}</Field>
              {s.input_hash_short && (
                <Field label="input hash">{s.input_hash_short}…</Field>)}
            </div>
          ))}
        </div>
        <div className="text-[10px] text-muted-foreground break-anywhere">
          Struttura segmenti (S0–S6) dalla configurazione autorizzata (dati
          statici). I valori di esecuzione compaiono solo quando lo stato
          reale del run risponde.
        </div>
      </div>
    );
  }
  if (!d.hardened_run_present) {
    return (
      <div className="border border-border p-2 text-[11px] text-muted-foreground break-anywhere"
           data-testid="segmented-hardened-empty">
        Nessun run segmentato temprato presente per questa lettura.
        In attesa della nuova autorizzazione Owner temprata (P1.7.2).
      </div>
    );
  }
  const acct = d.accounting || {};
  const orphan = (acct.markers || []).includes("POSSIBLE_ORPHAN_PROVIDER_CHARGE");
  const orphanResolved = (acct.markers || []).includes("POSSIBLE_ORPHAN_PROVIDER_CHARGE_RESOLVED");
  // segmenti con POST accettata ma esito non confermato: la response_id e' persistita se presente nel record segmento
  const pendingSegs = (d.segments || []).filter((s) => (s.send_count || 0) > 0 && s.state !== "COMPLETED");
  const orphanRespPersisted = pendingSegs.length > 0 && pendingSegs.every((s) => !!s.response_id_short);
  const orphanSegs = pendingSegs.map((s) => s.segment_id).join(", ") || "—";
  // Totali calcolati dai record segmento (mai da contatori di run)
  const postTotal = (d.segments || []).reduce((n, s) => n + (s.send_count || 0), 0);
  const getTotal = (d.segments || []).reduce((n, s) => n + (s.poll_attempts || 0), 0);
  const cols = ["Seg", "Stato", "POST", "GET", "Tok in", "Tok out", "Costo",
    "response_id", "Lease", "Motivo/Errore"];

  return (
    <div className="w-full min-w-0 border border-border p-2 space-y-2 max-w-full break-anywhere"
         data-testid="segmented-hardened-progress-panel">
      <div className="font-bold flex items-center gap-1.5 flex-wrap">
        <ShieldCheck size={13} className="shrink-0" />Run segmentato temprato
        <span className="text-[10px] font-normal text-muted-foreground break-anywhere" data-testid="segmented-hardened-version">{d.reader_version_candidate}</span>
        <span className="font-mono text-[10px] break-anywhere">{d.logical_reader_run_id}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-[11px]"
           data-testid="segmented-hardened-totals">
        <div>Stato run: <b data-testid="segmented-hardened-run-status">{d.run_status}</b></div>
        {d.incomplete_reason && <div className="sm:col-span-3 text-red-800 break-anywhere" data-testid="segmented-hardened-incomplete-reason">Motivo: {d.incomplete_reason}</div>}
        <div>Segmento corrente:{" "}
          <b data-testid="segmented-hardened-current">{d.current_segment_label}</b></div>
        <div>Costo confermato: <b>${d.real_cost_confirmed_usd}</b></div>
        <div>Budget residuo: <b>${d.budget_residual_usd}</b></div>
        <div>Riserva worst-case: <b>${d.worst_case_reserve_remaining_usd}</b></div>
        <div>Esposizione incerta:{" "}
          <b data-testid="segmented-hardened-uncertain">${acct.uncertain_exposure_usd ?? 0}</b></div>
        <div>Retry: <b>non disponibile</b></div>
        <div>Reinvio POST: <b>mai automatico</b></div>
        <div>POST totali (dai segmenti): <b data-testid="segmented-hardened-post-total">{postTotal}</b> · GET totali: <b data-testid="segmented-hardened-get-total">{getTotal}</b></div>
      </div>
      {orphan && (
        <div className={`flex items-start gap-1.5 text-[11px] px-2 py-1 border ${orphanResolved ? "bg-emerald-50 border-emerald-300 text-emerald-950" : "bg-fuchsia-50 border-fuchsia-300 text-fuchsia-950"}`}
             data-testid="segmented-hardened-orphan-marker">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span data-testid="segmented-hardened-orphan-text">Marker contabile <b>POSSIBLE_ORPHAN_PROVIDER_CHARGE</b>{orphanResolved ? "" : ` (${orphanSegs})`}: costo
            registrato $0, esposizione massima incerta ${acct.uncertain_exposure_usd ?? 0}
            {" "}{orphanResolved
              ? "(risolto)"
              : (orphanRespPersisted
                ? "(POST accettata, response_id persistita: esito provider non ancora confermato — recuperabile solo GET, solo diagnostico)"
                : "(POST accettata, response_id non persistita — solo diagnostico)")}.
            {orphanResolved && <span> <b>Risolto</b>: esito recuperato con GET-only sulla response_id persistita, costo confermato nel run (storno append-only, marker storico intatto).</span>}
          </span>
        </div>
      )}

      {/* Desktop: tabella in contenitore scorrevole */}
      <div className="hidden sm:block table-scroll" data-testid="segmented-hardened-table">
        <table className="w-full text-[10px]">
          <thead><tr className="border-b border-border bg-muted text-left">
            {cols.map((h) => <th key={h} className="px-1.5 py-1 font-medium whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {(d.segments || []).map((s) => (
              <tr key={s.segment_id} className="border-b border-border"
                  data-testid={`segmented-hardened-row-${s.segment_id}`}>
                <td className="px-1.5 py-1 font-bold whitespace-nowrap">{s.segment_id}
                  <span className="font-normal text-muted-foreground"> {s.component_code || ""}</span></td>
                <td className="px-1.5 py-1"><StateBadge st={s.state} sid={s.segment_id} /></td>
                <td className="px-1.5 py-1">{s.send_count}</td>
                <td className="px-1.5 py-1">{s.poll_attempts}</td>
                <td className="px-1.5 py-1">{s.input_tokens ?? "—"}</td>
                <td className="px-1.5 py-1">{s.output_tokens ?? "—"}</td>
                <td className="px-1.5 py-1">{s.real_cost_usd != null ? `$${s.real_cost_usd}` : "—"}</td>
                <td className="px-1.5 py-1 font-mono">{s.response_id_short || "—"}</td>
                <td className="px-1.5 py-1 whitespace-nowrap">{s.lease_worker || "—"}</td>
                <td className="px-1.5 py-1 text-red-800 break-anywhere">
                  {s.defer_reason || s.error_type || s.error || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: una card verticale per segmento (tutti i campi) */}
      <div className="sm:hidden space-y-2" data-testid="segmented-hardened-cards">
        {(d.segments || []).map((s) => (
          <div key={s.segment_id} className="border border-border p-2 text-[11px]"
               data-testid={`segmented-hardened-card-${s.segment_id}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-bold">{s.segment_id}
                <span className="font-normal text-muted-foreground"> {s.component_code || ""}</span></span>
              <StateBadge st={s.state} sid={s.segment_id} />
            </div>
            <Field label="POST">{s.send_count}</Field>
            <Field label="GET">{s.poll_attempts}</Field>
            <Field label="Token in / out">{(s.input_tokens ?? "—")} / {(s.output_tokens ?? "—")}</Field>
            <Field label="Costo">{s.real_cost_usd != null ? `$${s.real_cost_usd}` : "—"}</Field>
            <Field label="Durata">{s.duration_s != null ? `${s.duration_s}s` : "—"}</Field>
            <Field label="response_id">{s.response_id_short || "—"}</Field>
            <Field label="Lease / heartbeat">{s.lease_worker || "—"}{s.heartbeat_at ? ` · ${s.heartbeat_at.slice(11, 19)}` : ""}</Field>
            {(s.defer_reason || s.error_type || s.error) && (
              <Field label="Motivo/Errore">
                <span className="text-red-800">{s.defer_reason || s.error_type || s.error}</span>
              </Field>
            )}
          </div>
        ))}
      </div>

      <div className="text-[10px] text-muted-foreground break-anywhere">
        Vista read-only (solo polling GET). Una sola POST per segmento,
        mai ripetuta. Un errore di GET rinvia il polling (POLLING_DEFERRED)
        senza perdere la response_id. Esito finale obbligatorio {d.final_state_forced}.
      </div>
    </div>
  );
};
